/**
 * Workflow Page - Main application workflow
 *
 * Three-column layout matching desktop version:
 * - Left: PDF Viewer with hover magnification
 * - Middle: Data Entry Fields (Patient, Guarantor, Insurance)
 * - Right: Patient Match Panel (initially collapsed)
 * - Bottom: Navigation controls (Previous, Next, Connect to Tebra, etc.)
 */

import { useEffect, useState } from 'react';
import { Box, Paper, Alert, Button, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PDFViewer } from '../components/pdf/PDFViewer';
import { DataFieldsPanel } from '../components/patient/DataFieldsPanel';
import { PatientMatchPanel } from '../components/patient/PatientMatchPanel';
import { WorkflowControls } from '../components/workflow/WorkflowControls';
import { useSessionStore } from '../store/sessionStore';
import { usePDFStore } from '../store/pdfStore';
import { pdfsApi } from '../api/pdfs';
import { useSessionPolling } from '../hooks/useSessionPolling';

export const WorkflowPage: React.FC = () => {
  const navigate = useNavigate();
  const { activeSession, customerId } = useSessionStore();
  const {
    currentPDF,
    setCurrentPDF,
    currentPDFIndex,
    totalPDFs,
    navigateTo,
    setTotalPDFs,
    tebraWorkflowActive,
    setTebraWorkflowActive
  } = usePDFStore();

  const [showPatientPanel, setShowPatientPanel] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Auto-update session counters every 5 seconds
  useSessionPolling({ interval: 5000, enabled: !!activeSession });

  // Redirect to dashboard if no active session
  useEffect(() => {
    if (!activeSession) {
      navigate('/');
    }
  }, [activeSession, navigate]);

  // Sync totalPDFs from session counters (updated by polling every 5s)
  const polledTotalPdfs = activeSession?.counters?.total_pdfs;
  useEffect(() => {
    if (polledTotalPdfs != null && polledTotalPdfs > 0) {
      setTotalPDFs(polledTotalPdfs);
    }
  }, [polledTotalPdfs, setTotalPDFs]);

  // Load first PDF only when a new session starts (stable session_id, not object ref)
  const sessionId = activeSession?.session_id;
  useEffect(() => {
    if (sessionId && customerId) {
      loadPDFByIndex(0);
    }
  }, [sessionId, customerId]);

  const loadPDFByIndex = async (index: number) => {
    if (!activeSession) return;

    try {
      const response = await pdfsApi.navigateToPDF(
        activeSession.session_id,
        customerId,
        index
      );

      // Fetch PDF content through backend proxy (no direct blob storage access)
      let blobUrl: string | null = null;
      if (response.pdf_metadata.status !== 'Error') {
        const pdfBlob = await pdfsApi.getPDFContent(response.pdf_metadata.pdf_id);
        blobUrl = URL.createObjectURL(pdfBlob);
      }

      setCurrentPDF(
        response.pdf_metadata,
        response.extraction_results,
        blobUrl
      );

      // Update navigation state in store
      navigateTo(index);
      if (activeSession.counters?.total_pdfs != null) {
        setTotalPDFs(activeSession.counters.total_pdfs);
      }

      // Mark as viewed in background (fair billing trigger)
      if (response.pdf_metadata.status !== 'Error') {
        pdfsApi.markPDFViewed(response.pdf_metadata.pdf_id, customerId).catch((err) => {
          console.error('Failed to mark PDF as viewed:', err);
        });
      }
    } catch (error) {
      console.error('Failed to load PDF:', error);
    }
  };

  const handlePrevious = () => {
    if (currentPDFIndex > 0) {
      loadPDFByIndex(currentPDFIndex - 1);
    }
  };

  const handleNext = () => {
    if (totalPDFs === 0 || currentPDFIndex + 1 < totalPDFs) {
      loadPDFByIndex(currentPDFIndex + 1);
    }
  };

  const handleConnectToTebra = () => {
    setShowPatientPanel(true);
    setTebraWorkflowActive(true);
  };

  const handleRetryExtraction = async () => {
    if (!currentPDF || !customerId) return;

    setRetrying(true);
    try {
      await pdfsApi.extractPDF(currentPDF.pdf_id, customerId, true);
      // Reload current PDF data
      await loadPDFByIndex(currentPDFIndex);
    } catch (error) {
      console.error('Failed to retry extraction:', error);
    } finally {
      setRetrying(false);
    }
  };

  if (!activeSession) {
    return null;
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Error Alert if PDF failed */}
      {currentPDF?.status === 'Error' && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleRetryExtraction} disabled={retrying}>
              {retrying ? <CircularProgress size={20} color="inherit" /> : 'RETRY EXTRACTION'}
            </Button>
          }
          sx={{ borderRadius: 0 }}
        >
          <Typography variant="body2">
            <strong>Extraction Failed:</strong> {currentPDF.error_message || 'An unknown error occurred during processing.'}
          </Typography>
        </Alert>
      )}

      {/* Three-column layout */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {/* Left: PDF Viewer (50% when patient panel collapsed, 33% when expanded) */}
        <Box
          sx={{
            width: showPatientPanel ? '33.33%' : '50%',
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'auto',
            transition: 'width 0.3s ease',
            bgcolor: currentPDF?.status === 'Error' ? 'rgba(255, 0, 0, 0.02)' : 'inherit'
          }}
        >
          <PDFViewer />
        </Box>

        {/* Middle: Data Fields (50% when patient panel collapsed, 33% when expanded) */}
        <Box
          sx={{
            width: showPatientPanel ? '33.33%' : '50%',
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'auto',
            transition: 'width 0.3s ease',
            opacity: currentPDF?.status === 'Error' ? 0.6 : 1,
            pointerEvents: currentPDF?.status === 'Error' ? 'none' : 'auto'
          }}
        >
          <DataFieldsPanel />
        </Box>

        {/* Right: Patient Match Panel (0% collapsed, 33% expanded) */}
        {showPatientPanel && (
          <Box
            sx={{
              width: '33.33%',
              overflow: 'auto',
              transition: 'width 0.3s ease',
            }}
          >
            <PatientMatchPanel onAfterSave={handleNext} />
          </Box>
        )}
      </Box>

      {/* Bottom: Workflow Controls */}
      <Paper elevation={3} sx={{ borderTop: 1, borderColor: 'divider' }}>
        <WorkflowControls
          onPrevious={handlePrevious}
          onNext={handleNext}
          onConnectToTebra={handleConnectToTebra}
          tebraWorkflowActive={tebraWorkflowActive}
        />
      </Paper>
    </Box>
  );
};
